// The quote builder page, shared by the Quick Quote and detailed quote routes.
const QuotePage = () => import('@/views/pages/ClientPortal/SimpleQuotePage.vue');

const MainRoutes = {
    path: '/',
    meta: {
        requiresAuth: true
    },
    redirect: { name: 'dashboard' },
    component: () => import('@/layouts/full/FullLayout.vue'),
    children: [
        { isPage: false, name: 'ohs_overview', path: '/ohs/overview', component: () => import('@/views/pages/OHS/Overview/OhsOverview.vue'), meta: { title: 'Safety overview', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' } },
        { isPage: false, name: 'ohs_workers', path: '/ohs/workers', component: () => import('@/views/pages/OHS/Workers/WorkersRegister.vue'), meta: { title: 'Workers', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' } },
        { isPage: false, name: 'ohs_worker_detail', path: '/ohs/workers/:id', component: () => import('@/views/pages/OHS/Workers/WorkerDetail.vue'), meta: { title: 'Worker', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' } },
        { isPage: false, name: 'ohs_ticket_settings', path: '/ohs/settings/tickets', component: () => import('@/views/pages/OHS/Workers/TicketSettings.vue'), meta: { title: 'Ticket settings', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' } },
        { isPage: false, name: 'ohs_reports', path: '/ohs/reports', component: () => import('@/views/pages/OHS/Reports/ReportsRegister.vue'), meta: { title: 'Reports', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' } },
        { isPage: false, name: 'ohs_report_detail', path: '/ohs/reports/:id', component: () => import('@/views/pages/OHS/Reports/ReportDetail.vue'), meta: { title: 'Report', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' } },
        {
            isPage: false,
            name: 'ohs_induction_settings',
            path: '/ohs/settings/induction',
            component: () => import('@/views/pages/OHS/Induction/InductionBuilder.vue'),
            meta: { title: 'Company induction', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' }
        },
        {
            isPage: false,
            name: 'ohs_site',
            path: '/ohs/site/:jobId',
            component: () => import('@/views/pages/OHS/Site/SiteSafety.vue'),
            meta: { title: 'Site safety', requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module' }
        },
        {
            isPage: false,
            name: 'dashboard',
            path: '/dashboard',
            component: () => import('@/views/pages/Dashboard/index.vue'),
            meta: {
                title: 'Home',
                requiresAuth: true
            }
        },
        {
            /*
             * The Quotes list.
             *
             * Still named `client_portal`, and that is deliberate rather than
             * leftover. A nav item's `to` is BOTH the router destination and
             * the permission key -- NavItem gates visibility on
             * `checkrole(item.to)`, which looks up `item.to + '__View'`;
             * roles.ts authors the permission checkboxes from the same string;
             * and the router guard gates deep links on it for any `isPage`
             * route. Every role in `user_roles.role_settings` was written with
             * `client_portal__View`, and `hasPermission` fails CLOSED, so
             * renaming this to `quotes` would have dropped the page out of the
             * nav for every user who is not a Full Admin -- invisibly, because
             * `isFullAdmin` and `user.id == 1` bypass the check, which is
             * exactly who tests it.
             *
             * So the route keeps its name and gains the new component plus a
             * `/quotes` alias. `/smart-quote` still works; nobody's permissions
             * move.
             */
            isPage: true,
            name: 'client_portal',
            path: '/smart-quote',
            alias: '/quotes',
            component: () => import('@/views/pages/Quotes/index.vue'),
            /*
             * `?job=` used to mean "open that job's detail" -- ClientPortal
             * rendered the list and swapped it for the detail when the query
             * was present. The Quotes page reads only `?tab=`, and an
             * unrecognised query param is ignored rather than rejected, so
             * without this a `/smart-quote?job=<id>` link navigates fine and
             * lands the user on a generic list with the id dangling in the URL
             * and no error at all.
             *
             * Handled here rather than by editing the callers because two
             * classes of caller cannot be edited: URLs already bookmarked, and
             * links emitted outside this repo. One redirect covers every
             * producer, including the ones that do not exist yet.
             */
            beforeEnter: (to: any) =>
                to.query?.job ? { path: '/smart-quote-detail', query: to.query } : true,
            meta: {
                title: 'Quotes',
                requiresAuth: true,
                featureKey: 'quotes' // runtime feature flag — see stores/featureFlags
            }
        },
        {
            // Live P&L (Epic 4): cross-job portfolio.
            isPage: true,
            name: 'job_portfolio',
            path: '/job-portfolio',
            component: () => import('@/views/pages/Portfolio/index.vue'),
            meta: {
                title: 'Job Financials',
                requiresAuth: true
            }
        },
        {
            // Quick Quote builder (#514): a simple-mode quote on its own page.
            // Named under client_portal so it is gated on the quotes list
            // permission — see SIMPLE_QUOTE_ROUTE_NAME in quotePageShell.
            isPage: true,
            name: 'client_portal?builder=:jobId',
            path: '/smart-quote/:jobId/quote',
            component: QuotePage,
            meta: {
                title: 'Quote',
                requiresAuth: true,
                featureKey: 'quotes'
            }
        },
        {
            // Detailed quote builder: a detailed quote on its own page, full
            // screen like the Quick Quote builder above (the same component,
            // making detailed quotes). Gated on the quotes list permission the
            // same way — see DETAILED_QUOTE_ROUTE_NAME in quotePageShell.
            isPage: true,
            name: 'client_portal?detailed=:jobId',
            path: '/smart-quote/:jobId/detailed',
            component: QuotePage,
            props: { mode: 'detailed' },
            meta: {
                title: 'Detailed quote',
                requiresAuth: true,
                featureKey: 'quotes'
            }
        },
        {
            // Same component as /smart-quote, so it needs the same gate. Mapped
            // to `client_portal` (the /smart-quote key) via
            // PAGE_PERMISSION_ALIASES, because no role carries a
            // `client_portal_detail__View` key.
            isPage: true,
            name: 'client_portal_detail?job=:id&tab=:value',
            path: '/smart-quote-detail',
            component: () => import('@/views/pages/ClientPortal/index.vue'),
            meta: {
                title: 'Quotes',
                requiresAuth: true,
                featureKey: 'quotes'
            }
        },
        {
            // BS-004: both analytics routes are part of the Quotes module and
            // were reachable by typed URL with the module switched off — the
            // second one doubly so, since isPage:false keeps it out of
            // PERMISSIONED_PAGES and it therefore had no gate of any kind.
            isPage: true,
            name: 'tender_analytics',
            path: '/tender-analytics',
            component: () => import('@/views/pages/ClientPortal/analytics/TenderDashboard.vue'),
            meta: {
                title: 'Tender Analytics',
                requiresAuth: true,
                featureKey: 'quotes'
            }
        },
        {
            isPage: false,
            name: 'tender_detailed_analytics',
            path: '/tender-analytics/:id',
            component: () => import('@/views/pages/ClientPortal/analytics/TenderDetailedAnalytics.vue'),
            meta: {
                title: 'Detailed Analytics',
                requiresAuth: true,
                featureKey: 'quotes'
            }
        },
        {
            isPage: true,
            name: 'notifications',
            path: '/notifications',
            component: () => import('@/views/pages/Notifications/index.vue'),
            meta: {
                title: 'Notifications',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'notification-detail',
            path: '/notifications/:id',
            component: () => import('@/views/pages/Notifications/NotificationDetail.vue'),
            meta: {
                title: 'Notification Details',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'schedule_jobs?project=:id&edit=:id',
            path: '/work-schedules',
            component: () => import('@/views/pages/ScheduleJobs/index.vue'),
            meta: {
                title: 'Schedule Projects',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'schedules',
            path: '/projects-schedules',
            component: () => import('@/views/pages/JobSchedules/index.vue'),
            meta: {
                title: 'Schedules',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'jobs',
            path: '/projects',
            component: () => import('@/views/pages/Jobs/index.vue'),
            meta: {
                title: 'Projects',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'estimates',
            path: '/estimates',
            component: () => import('@/views/pages/Estimates/index.vue'),
            meta: {
                title: 'Estimates',
                requiresAuth: true
            }
        },
        {
            // Gated on `estimates__View` via PAGE_PERMISSION_ALIASES in
            // router/index.ts -- the derived `estimate_list__View` key does not
            // exist on any role.
            isPage: true,
            name: 'estimate_list?list=:id&?tab=:value',
            path: '/estimate-list',
            // The host opens another job fresh when only ?list= changes (see jobPageHost.ts).
            component: () =>
                Promise.all([import('@/views/pages/EstimateList/jobPageHost'), import('@/views/pages/EstimateList/index.vue')]).then(
                    ([{ jobPageHost }, page]) => jobPageHost(page.default)
                ),
            meta: {
                title: 'Estimates',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'view_pdfplan?list=:id&?page=:number&?subid=:number&?category=:number&?index=:number&?key=:number&?tab=:string&?value=:number',
            path: '/view-pdfplan',
            component: () => import('@/views/pages/EstimateList/ViewPlans/index.vue'),
            meta: {
                title: 'Estimates',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'view_pdfplan_original?list=:id&?page=:number&?subid=:number&?category=:number&?index=:number&?key=:number&?tab=:string&?value=:number',
            path: '/view-pdfplan-original',
            component: () => import('@/views/pages/EstimateList/ViewPlansOriginal/index.vue'),
            meta: {
                title: 'Estimates',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'costing_pdfplan?list=:id&?page=:number',
            path: '/costing-pdfplan',
            component: () => import('@/views/pages/EstimateList/CostingPdf/index.vue'),
            meta: {
                title: 'Estimates',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'SearchResults',
            path: '/search',
            component: () => import('@/views/pages/SearchResults/index.vue'),
            meta: {
                title: 'Search Results',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'notes',
            path: '/notes',
            component: () => import('@/views/pages/Notes/index.vue'),
            meta: {
                title: 'Project Notes',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'calculator',
            path: '/calculator',
            component: () => import('@/views/pages/Calculator/index.vue'),
            meta: {
                title: 'Calculators',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'ncc_assistant',
            path: '/ncc-assistant',
            component: () => import('@/views/pages/NCCSearch/index.vue'),
            meta: {
                title: 'NCC Code Assistant',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'ncc_details',
            path: '/ncc-details',
            component: () => import('@/views/pages/NCCDetails/index.vue'),
            meta: {
                title: 'NCC Visual Details',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'ncc_reference',
            path: '/ncc-reference',
            component: () => import('@/views/pages/NCCReference/index.vue'),
            meta: {
                title: 'NCC Reference',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'file_manager',
            path: '/file-manager',
            component: () => import('@/views/pages/FileManager/index.vue'),
            meta: {
                title: 'Storage',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'to_dos',
            path: '/to-dos',
            component: () => import('@/views/pages/Todos/index.vue'),
            meta: {
                title: 'To Dos',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'internal_users',
            path: '/internal-users',
            component: () => import('@/views/pages/InternalUsers/index.vue'),
            meta: {
                title: 'Internal Users',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'build_board',
            path: '/build-board',
            component: () => import('@/views/pages/BuildBoard/index.vue'),
            meta: {
                title: 'Roadmap',
                requiresAuth: true
            }
        },

        {
            isPage: true,
            name: 'customers',
            path: '/customers',
            component: () => import('@/views/pages/Customers/index.vue'),
            meta: {
                title: 'Customers',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'suppliers',
            path: '/suppliers',
            component: () => import('@/views/pages/Suppliers/index.vue'),
            meta: {
                title: 'Suppliers & Sub contractors',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'bulk_import',
            path: '/bulk-import',
            component: () => import('@/views/pages/BulkImport/index.vue'),
            meta: {
                title: 'Bulk Import',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'account_settings',
            path: '/account-settings',
            component: () => import('@/views/pages/AccountSettings/index.vue'),
            meta: {
                title: 'Account Settings',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'sub_account_settings',
            path: '/sub-account-settings',
            component: () => import('@/views/pages/AccountSubSettings/index.vue'),
            meta: {
                title: 'Account Settings',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'company_settings',
            path: '/company-settings',
            component: () => import('@/views/pages/CompanySettings/index.vue'),
            meta: {
                title: 'Company Settings',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'subscription_settings',
            path: '/subscription-settings',
            component: () => import('@/views/pages/SubscriptionSettings/index.vue'),
            meta: {
                title: 'Subscription Settings',
                requiresAuth: true
            }
        },

        {
            isPage: true,
            name: 'user_roles',
            path: '/user-roles',
            component: () => import('@/views/pages/UserRoles/index.vue'),
            meta: {
                title: 'Manage Roles',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'summary_jobs?summary=:id',
            path: '/summary-jobs',
            component: () => import('@/views/pages/Jobs/Summary.vue'),
            meta: {
                title: 'Job Summary',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'customer_portal?project=:id',
            path: '/customer-portal',
            component: () => import('@/views/pages/Jobs/CustomerPortal.vue'),
            meta: {
                title: 'Customer Portal',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'calendar_sub?project=:id',
            path: '/calendar-sub',
            component: () => import('@/views/pages/Jobs/components/SubVendors/Schedules/index.vue'),
            meta: {
                title: 'Schedule Summary',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'schedulelist?status=:value',
            path: '/schedules-list',
            component: () => import('@/views/pages/Jobs/components/SubVendors/Schedules/list.vue'),
            meta: {
                title: 'Schedule List',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'schedules_sub?summary=:id',
            path: '/schedules-sub',
            component: () => import('@/views/pages/Jobs/components/SubVendors/Summary.vue'),
            meta: {
                title: 'Schedule Summary',
                requiresAuth: true
            }
        },

        {
            isPage: false,
            name: 'jobs_sub',
            path: '/jobs-sub',
            component: () => import('@/views/pages/Jobs/components/SubVendors/Jobs/index.vue'),
            meta: {
                title: 'Projects',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'todos_jobs',
            path: '/todos-sub',
            component: () => import('@/views/pages/Jobs/components/SubVendors/Todos/index.vue'),
            meta: {
                title: 'Daily Logs',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'teams',
            path: '/teams',
            component: () => import('@/views/pages/Teams/TeamOverview/index.vue'),
            meta: {
                title: 'Team Tracker',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'team_tracker',
            path: '/team-tracker',
            component: () => import('@/views/pages/Teams/Overview/index.vue'),
            meta: {
                title: 'Team Tracker',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'team_view_details?week=:name&?project=:name',
            path: '/team-view-details',
            component: () => import('@/views/pages/Teams/ViewDetails/index.vue'),
            meta: {
                title: 'View Details',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'time_sheet?week=:name&?project=:name&?assigned_id=:id',
            path: '/time-sheet',
            component: () => import('@/views/pages/Teams/TimeSheet/index.vue'),
            meta: {
                title: 'View Details',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'reconciliation_adjustment_details?week=:name',
            path: '/reconciliation-adjustment-details',
            component: () => import('@/views/pages/Teams/Overview/ReconciliationAdjustmentDetails.vue'),
            meta: {
                title: 'Reconciliation Adjustment Details',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'reconciliation_adjusted_details?week=:name&version_id=:id',
            path: '/reconciliation-adjusted-details',
            component: () => import('@/views/pages/Teams/Overview/ReconciliationAdjustedDetails.vue'),
            meta: {
                title: 'Reconciliation Adjusted Details',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'equipment_tracker',
            path: '/equipment-tracker',
            component: () => import('@/views/pages/Equipments/Overview/index.vue'),
            meta: {
                title: 'Equipment Tracker',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'equipments',
            path: '/equipments',
            component: () => import('@/views/pages/Equipments/index.vue'),
            meta: {
                title: 'Equipment',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'ohs',
            path: '/ohs',
            component: () => import('@/views/pages/OHS/index.vue'),
            meta: {
                title: 'SWMS',
                requiresAuth: true, permission: 'ohs', planFeature: 'ohs_module'
            }
        },
        {
            isPage: true,
            name: 'costings',
            path: '/costings',
            component: () => import('@/views/pages/Costings/index.vue'),
            meta: {
                title: 'Costings',
                requiresAuth: true
            }
        },
        {
            isPage: true,
            name: 'defects',
            path: '/defects',
            component: () => import('@/views/pages/Defects/index.vue'),
            meta: {
                title: 'Defects',
                requiresAuth: true
            }
        },
        // Mat AI
        {
            isPage: true,
            name: 'mat_ai',
            path: '/mat-ai',
            component: () => import('@/views/pages/MatAI/index.vue'),
            meta: {
                title: 'Mat AI',
                requiresAuth: true,
                featureKey: 'mat_ai' // runtime feature flag — see stores/featureFlags
            }
        },
        // Developer Portal
        {
            isPage: true,
            name: 'developer_portal',
            path: '/developer-portal',
            component: () => import('@/views/pages/DeveloperPortal/index.vue'),
            meta: {
                title: 'Developer Portal',
                requiresAuth: true,
                requiresAdmin: true // see the requiresAdmin gate in router/index.ts
            }
        },
        // Help Center
        {
            isPage: false,
            name: 'help_center',
            path: '/help-center',
            component: () => import('@/views/pages/HelpCenter/index.vue'),
            meta: {
                title: 'Help Centre',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'help_article',
            path: '/help-center/article/:slug',
            component: () => import('@/views/pages/HelpCenter/ArticleView.vue'),
            meta: {
                title: 'Help Article',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'help_guide',
            path: '/help-center/guide/:slug',
            component: () => import('@/views/pages/HelpCenter/GuideView.vue'),
            meta: {
                title: 'Step-by-Step Guide',
                requiresAuth: true
            }
        },
        {
            // Authoring help content. Gated on `help_center_admin` via
            // PAGE_PERMISSION_ALIASES -- these route names are not catalogue keys.
            isPage: true,
            name: 'help_admin',
            path: '/help-center/admin',
            component: () => import('@/views/pages/HelpCenter/Admin/index.vue'),
            meta: {
                title: 'Help Centre Admin',
                requiresAuth: true
            }
        },
        {
            // Authoring help content. Gated on `help_center_admin` via
            // PAGE_PERMISSION_ALIASES -- these route names are not catalogue keys.
            isPage: true,
            name: 'help_admin_article_create',
            path: '/help-center/admin/article/create',
            component: () => import('@/views/pages/HelpCenter/Admin/ArticleEditor.vue'),
            meta: {
                title: 'Create Article',
                requiresAuth: true
            }
        },
        {
            // Authoring help content. Gated on `help_center_admin` via
            // PAGE_PERMISSION_ALIASES -- these route names are not catalogue keys.
            isPage: true,
            name: 'help_admin_article_edit',
            path: '/help-center/admin/article/:id/edit',
            component: () => import('@/views/pages/HelpCenter/Admin/ArticleEditor.vue'),
            meta: {
                title: 'Edit Article',
                requiresAuth: true
            }
        },
        {
            // Authoring help content. Gated on `help_center_admin` via
            // PAGE_PERMISSION_ALIASES -- these route names are not catalogue keys.
            isPage: true,
            name: 'help_admin_guide_create',
            path: '/help-center/admin/guide/create',
            component: () => import('@/views/pages/HelpCenter/Admin/GuideEditor.vue'),
            meta: {
                title: 'Create Guide',
                requiresAuth: true
            }
        },
        {
            // Authoring help content. Gated on `help_center_admin` via
            // PAGE_PERMISSION_ALIASES -- these route names are not catalogue keys.
            isPage: true,
            name: 'help_admin_guide_edit',
            path: '/help-center/admin/guide/:id/edit',
            component: () => import('@/views/pages/HelpCenter/Admin/GuideEditor.vue'),
            meta: {
                title: 'Edit Guide',
                requiresAuth: true
            }
        },
        {
            // Authoring help content. Gated on `help_center_admin` via
            // PAGE_PERMISSION_ALIASES -- these route names are not catalogue keys.
            isPage: true,
            name: 'help_admin_tour_create',
            path: '/help-center/admin/tour/create',
            component: () => import('@/views/pages/HelpCenter/Admin/TourEditor.vue'),
            meta: {
                title: 'Create Tour',
                requiresAuth: true
            }
        },
        {
            // Authoring help content. Gated on `help_center_admin` via
            // PAGE_PERMISSION_ALIASES -- these route names are not catalogue keys.
            isPage: true,
            name: 'help_admin_tour_edit',
            path: '/help-center/admin/tour/:id/edit',
            component: () => import('@/views/pages/HelpCenter/Admin/TourEditor.vue'),
            meta: {
                title: 'Edit Tour',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'help_category',
            path: '/help-center/category/:slug',
            component: () => import('@/views/pages/HelpCenter/CategoryView.vue'),
            meta: {
                title: 'Help Category',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'help_faq',
            path: '/help-center/faq',
            component: () => import('@/views/pages/HelpCenter/FAQPage.vue'),
            meta: {
                title: 'Frequently Asked Questions',
                requiresAuth: true
            }
        },
        // Admin Insights — at-risk trial customers
        {
            isPage: true,
            name: 'at_risk_trials',
            path: '/admin/at-risk-trials',
            component: () => import('@/views/pages/AdminInsights/AtRiskTrials.vue'),
            meta: {
                title: 'At-Risk Trials',
                requiresAuth: true,
                requiresAdmin: true // see the requiresAdmin gate in router/index.ts
            }
        },
        // PDF Prototype (from main)
        {
            isPage: false,
            name: 'pdf_prototype',
            path: '/pdf-prototype',
            component: () => import('@/views/pages/PdfPrototype/index-v3.vue'),
            meta: {
                title: 'PDF.js Prototype - With Drawing & Scale',
                requiresAuth: true
            }
        },
        {
            isPage: false,
            name: 'viewplans_demo',
            path: '/viewplans-demo',
            component: () => import('@/views/pages/PdfPrototype/ViewPlansDemo.vue'),
            meta: {
                title: 'ViewPlans Demo - Full Takeoff Module with PDF.js',
                requiresAuth: true
            }
        }
    ]
};

export default MainRoutes;
